import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useElementWidth } from 'hooks/useElementWidth';

import { getToolbarActionDistribution } from './ResumeSearchToolbar.utils';

const EMPTY_DISTRIBUTION = { inlineActionKeys: [], overflowActionKeys: [] };
const GAP = 8;
const CHIP_GAP = 8;
const OVERFLOW_TRIGGER_WIDTH = 40;

/**
 * Owns the toolbar's measurement, action distribution, overflow-menu state, and reflow focus.
 * The pure fit calculation lives in ResumeSearchToolbar.utils; container width comes from the
 * shared useElementWidth primitive.
 */
export const useResponsiveToolbar = ({
  actions,
  activeSearch,
  isLoading,
  dateRangeValue,
  sortId
}) => {
  const { ref: observeRef, width } = useElementWidth();
  const rootRef = useRef(null);
  const filtersRef = useRef(null);
  const chipRef = useRef(null);
  const triggerRef = useRef(null);
  const measurementRefs = useRef({});
  const previousOverflowRef = useRef([]);
  const pendingFocusRef = useRef(null);

  const [distribution, setDistribution] = useState(EMPTY_DISTRIBUTION);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);

  const setRootRef = useCallback(
    node => {
      rootRef.current = node;
      observeRef(node);
    },
    [observeRef]
  );

  const registerMeasurementRef = useCallback(
    key => node => {
      measurementRefs.current[key] = node;
    },
    []
  );

  const actionKeys = useMemo(() => actions.map(action => action.key), [actions]);

  const actionsSignature = useMemo(
    () => actions.map(action => `${action.key}:${action.text}`).join('|'),
    [actions]
  );

  useEffect(() => {
    if (isLoading || !width) return;
    const actionWidths = actionKeys.reduce(
      (widths, key) => ({ ...widths, [key]: measurementRefs.current[key]?.offsetWidth || 0 }),
      {}
    );
    const fixedWidth =
      (filtersRef.current?.offsetWidth || 0) +
      (chipRef.current?.offsetWidth || 0) +
      (activeSearch ? CHIP_GAP : 0);

    const next = getToolbarActionDistribution({
      actionKeys,
      actionWidths,
      availableWidth: width,
      fixedWidth,
      gapWidth: GAP,
      overflowTriggerWidth: OVERFLOW_TRIGGER_WIDTH
    });

    const prevOverflow = previousOverflowRef.current;
    if (next.overflowActionKeys.join('|') !== prevOverflow.join('|')) {
      const active = document.activeElement;
      const focusedKey = active?.dataset?.toolbarActionKey;
      if (focusedKey && next.overflowActionKeys.includes(focusedKey)) {
        pendingFocusRef.current = { type: 'trigger' };
      } else if (
        active === triggerRef.current &&
        prevOverflow.length &&
        !next.overflowActionKeys.length
      ) {
        const restoredKey = prevOverflow.find(key => next.inlineActionKeys.includes(key));
        pendingFocusRef.current = restoredKey ? { type: 'action', key: restoredKey } : null;
      }
      previousOverflowRef.current = next.overflowActionKeys;
      setIsOverflowOpen(false);
    }

    setDistribution(prev =>
      prev.inlineActionKeys.join('|') === next.inlineActionKeys.join('|') ? prev : next
    );
  }, [width, actionKeys, actionsSignature, activeSearch, isLoading, dateRangeValue, sortId]);

  useLayoutEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    pendingFocusRef.current = null;
    if (pending.type === 'trigger') {
      triggerRef.current?.focus();
    } else {
      rootRef.current?.querySelector(`[data-toolbar-action-key="${pending.key}"]`)?.focus();
    }
  }, [distribution]);

  const inlineActionKeys = actionKeys.filter(key => distribution.inlineActionKeys.includes(key));
  const overflowActionKeys = actionKeys.filter(key => !inlineActionKeys.includes(key));

  return {
    setRootRef,
    filtersRef,
    chipRef,
    triggerRef,
    registerMeasurementRef,
    inlineActionKeys,
    overflowActionKeys,
    isOverflowOpen,
    setIsOverflowOpen
  };
};
